import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  salesInvoiceService,
  CreateSalesInvoiceLineRequest,
  CreateSalesInvoiceRequest,
  CreateAndPaySalesInvoiceRequest,
} from '../../../../services/salesInvoiceService';
import { customerService, CustomerDTO } from '../../../../services/customerService';
import { salesItemService, SalesItemDTO } from '../../../../services/salesItemService';
import { serviceItemService, ServiceItemDTO } from '../../../../services/serviceItemService';
import paymentTypeService, { PaymentType } from '../../../../services/paymentTypeService';
import { companySettingsService } from '../../../../services/companySettingsService';
import { businessDayService } from '../../../../services/businessDayService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import './SalesInvoicePage.css';
import { CircleX, Printer, Undo2, ArrowLeft, Plus, Info, Scan, Wrench } from 'lucide-react';
import { parseApiError } from '../../../../utils/errorUtils';
import {
  calcLineDiscountAmount,
  calcNetUnitPrice,
  isBelowMinimumPrice,
} from '../../utils/minimumSellingPrice';

// ─── Types ────────────────────────────────────────────────────────────────────

type DiscountType = 'fixed' | 'percent';

interface LineRow {
  lineType:             'Product' | 'Service';
  salesItemId?:         number;
  productId:            number;
  serviceItemId?:       number;
  productName:          string;
  productSKU:           string;
  description?:         string;
  quantity:             number;
  unitPrice:            number;
  minimumSellingPrice?: number;
  discountType:         DiscountType;
  discountValue:        number;
  taxRate:              number;
}

interface InvoiceDiscount {
  type:  DiscountType;
  value: number;
}

const emptyLine = (): LineRow => ({
  lineType:      'Product',
  salesItemId:   undefined,
  productId:     0,
  productName:   '',
  productSKU:    '',
  quantity:      1,
  unitPrice:     0,
  discountType:  'fixed',
  discountValue: 0,
  taxRate:       0,
});

const emptyServiceLine = (): LineRow => ({
  lineType:      'Service',
  productId:     0,
  serviceItemId: undefined,
  productName:   '',
  productSKU:    '',
  description:   '',
  quantity:      1,
  unitPrice:     0,
  discountType:  'fixed',
  discountValue: 0,
  taxRate:       0,
});

/** Keyboard-navigable columns per row: qty, unitPrice, discount */
const COL_COUNT = 3;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * In cashier mode the page is embedded inside CashierLayout (its own shell), so it
 * renders without the admin Layout chrome. Otherwise it wraps in the admin Layout.
 */
const PageShell: React.FC<{ cashier: boolean; children: React.ReactNode }> = ({ cashier, children }) =>
  cashier ? <>{children}</> : <Layout>{children}</Layout>;

interface SalesInvoiceCreatePageProps {
  /** POS mode for cashier users: no admin chrome, and each completed sale clears the
   *  form for the next customer instead of navigating back to the admin invoice list. */
  cashierMode?: boolean;
}

const SalesInvoiceCreatePage: React.FC<SalesInvoiceCreatePageProps> = ({ cashierMode = false }) => {
  const navigate  = useNavigate();
  const location   = useLocation();
  const { t }     = useTranslation();

  const preselectedCustomerId = (location.state as { customerId?: number } | null)?.customerId;

  const [posMessage, setPosMessage] = useState<string | null>(null);
  // Set when the outlet's trading day is past its close cutoff — sales are blocked until
  // day-end runs. The backend enforces this too; this is the proactive POS reminder.
  const [dayEndRequired, setDayEndRequired] = useState(false);

  const [printing,              setPrinting]              = useState(false);
  const [error,                 setError]                 = useState<string | null>(null);
  const [paymentMethod,         setPaymentMethod]         = useState<string>('');
  const [amountTendered,        setAmountTendered]        = useState<number>(0);
  const [referenceNumber,       setReferenceNumber]       = useState<string>('');
  const [invoiceRefNumber,      setInvoiceRefNumber]      = useState<string>('');
  const [invoiceRefLabel,       setInvoiceRefLabel]       = useState<string>('');
  const [invoiceRefEnabled,     setInvoiceRefEnabled]     = useState(false);
  const [customers,             setCustomers]             = useState<CustomerDTO[]>([]);
  const [salesItems,            setSalesItems]            = useState<SalesItemDTO[]>([]);
  const [serviceItems,          setServiceItems]          = useState<ServiceItemDTO[]>([]);
  const [paymentTypes,          setPaymentTypes]          = useState<PaymentType[]>([]);
  const [loadingItems,          setLoadingItems]          = useState(false);
  const [barcodeInput,          setBarcodeInput]          = useState('');
  const [barcodeError,          setBarcodeError]          = useState<string | null>(null);
  const [invoiceDiscount,       setInvoiceDiscount]       = useState<InvoiceDiscount>({ type: 'fixed', value: 0 });

  const barcodeRef = useRef<HTMLInputElement>(null);
  /** cellRefs[rowIndex][colIndex] — 0=qty, 1=unitPrice, 2=discount */
  const cellRefs = useRef<(HTMLInputElement | null)[][]>([]);

  const [formData, setFormData] = useState({
    customerId:   preselectedCustomerId ? String(preselectedCustomerId) : '',
    invoiceDate:  '',
    dueDate:      '',
    notes:        '',
  });

  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);

  // ── Derived: selected payment type category ────────────────────────────────
  const selectedPaymentType = paymentTypes.find((pt) => pt.code === paymentMethod);
  const isCashPayment   = selectedPaymentType?.paymentCategory === 'Cash';
  const isCreditPayment = selectedPaymentType?.paymentCategory === 'Credit';

  // ── Load business date from settings ─────────────────────────────────────
  useEffect(() => {
    companySettingsService
      .getBusinessDate()
      .then((res) => {
        const data = res?.data?.data as {
          businessDate?: string;
          invoiceReferenceFieldEnabled?: boolean;
          invoiceReferenceFieldLabel?: string | null;
        } | undefined;
        const date = data?.businessDate;
        if (date) {
          const due = new Date(date);
          due.setDate(due.getDate() + 30);
          setFormData((prev) => ({
            ...prev,
            invoiceDate: date,
            dueDate:     due.toISOString().slice(0, 10),
          }));
        }
        // Invoice-reference config also arrives here (no Settings.View needed), so the
        // cashier POS can render the automobile "Vehicle Number" field. The admin path
        // still refreshes it from full company settings below.
        if (data?.invoiceReferenceFieldEnabled !== undefined) {
          setInvoiceRefEnabled(!!data.invoiceReferenceFieldEnabled);
          if (data.invoiceReferenceFieldEnabled) {
            setInvoiceRefLabel(data.invoiceReferenceFieldLabel || t('salesInvoice.invoiceReferenceNumber'));
          }
        }
      })
      .catch(() => {
        const fallback = new Date().toISOString().slice(0, 10);
        const due      = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
        setFormData((prev) => ({ ...prev, invoiceDate: fallback, dueDate: due }));
      });
  }, []);

  // ── Load customers, items, payment types, and company settings ───────────
  useEffect(() => {
    customerService
      .getCustomers({ pageNumber: 1, pageSize: 500 })
      .then((res) => {
        const data = res?.data?.data;
        if (Array.isArray(data)) {
          setCustomers(data);
          if (!preselectedCustomerId) {
            const defaultCustomer = data.find((c) => c.isDefault);
            if (defaultCustomer) {
              setFormData((prev) => ({ ...prev, customerId: String(defaultCustomer.id) }));
            }
          }
        }
      })
      .catch(() => {});

    setLoadingItems(true);
    salesItemService
      .getSalesItems(1, 500)
      .then((res) => {
        const paginated = res?.data?.data as any;
        const data = Array.isArray(paginated)
          ? paginated
          : (paginated?.data ?? []);
        if (Array.isArray(data)) {
          setSalesItems(
            [...data].sort((a, b) =>
              (a.productName ?? '').localeCompare(b.productName ?? '', undefined, { sensitivity: 'base' })
            )
          );
        }
      })
      .catch(() => {})
      .finally(() => setLoadingItems(false));

    serviceItemService
      .getAll()
      .then((res) => {
        const data = (res?.data as any)?.data ?? res?.data;
        if (Array.isArray(data)) setServiceItems(data.filter((s: ServiceItemDTO) => s.isActive));
      })
      .catch(() => {});

    // Cashiers lack Settings.View; the invoice-reference config already arrived via
    // the business-date endpoint above, so only the admin path reads full settings.
    if (!cashierMode) {
      companySettingsService
        .getCompanySettings()
        .then((res) => {
          const settings = (res?.data as any)?.data ?? res?.data;
          if (settings) {
            setInvoiceRefEnabled(!!settings.invoiceReferenceFieldEnabled);
            setInvoiceRefLabel(settings.invoiceReferenceFieldLabel || t('salesInvoice.invoiceReferenceNumber'));
          }
        })
        .catch(() => {});
    }

    // Load active payment types and pre-select the default one
    paymentTypeService
      .getActivePaymentTypes('Sales')
      .then((types) => {
        if (!Array.isArray(types) || types.length === 0) return;
        setPaymentTypes(types);
        const defaultType = types.find((pt) => pt.isDefault) ?? types[0];
        setPaymentMethod(defaultType.code);
      })
      .catch(() => {
        setPaymentMethod('Cash');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactively warn/block if the outlet's trading day is past its close cutoff.
  useEffect(() => {
    if (!cashierMode) return;
    businessDayService
      .getCurrentStatus()
      .then((res) => setDayEndRequired(!!res?.data?.data?.requiresDayEnd))
      .catch(() => {});
  }, [cashierMode]);

  // ── Form change handler ────────────────────────────────────────────────────
  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Cell ref registration ──────────────────────────────────────────────────
  const setCellRef = useCallback(
    (rowIndex: number, colIndex: number) => (el: HTMLInputElement | null) => {
      if (!cellRefs.current[rowIndex]) cellRefs.current[rowIndex] = [];
      cellRefs.current[rowIndex][colIndex] = el;
    },
    []
  );

  // ── Keyboard navigation (Tab / Enter move between numeric cells) ───────────
  const handleCellKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    colIndex: number
  ) => {
    const isForward  = e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey);
    const isBackward = e.key === 'Tab' && e.shiftKey;
    if (!isForward && !isBackward) return;

    e.preventDefault();

    if (isForward) {
      const nextCol = colIndex + 1;
      if (nextCol < COL_COUNT) {
        cellRefs.current[rowIndex]?.[nextCol]?.focus();
      } else if (rowIndex + 1 < lines.length) {
        cellRefs.current[rowIndex + 1]?.[0]?.focus();
      } else {
        setLines((prev) => [...prev, emptyLine()]);
        setTimeout(() => cellRefs.current[rowIndex + 1]?.[0]?.focus(), 60);
      }
    } else {
      const prevCol = colIndex - 1;
      if (prevCol >= 0) {
        cellRefs.current[rowIndex]?.[prevCol]?.focus();
      } else if (rowIndex > 0) {
        cellRefs.current[rowIndex - 1]?.[COL_COUNT - 1]?.focus();
      }
    }
  };

  // ── Line item select ───────────────────────────────────────────────────────
  const handleLineItemSelect = (index: number, salesItemId: string) => {
    const item = salesItems.find((s) => s.salesItemId === Number(salesItemId));
    setLines((prev) => {
      const updated = [...prev];
      if (item) {
        updated[index] = {
          salesItemId:          item.salesItemId,
          productId:            item.productId,
          productName:          item.productName,
          productSKU:           item.productSKU,
          quantity:             updated[index].quantity || 1,
          unitPrice:            item.sellingPrice,
          minimumSellingPrice:  item.minimumSellingPrice ?? undefined,
          discountType:         'fixed',
          discountValue:        0,
          taxRate:              item.taxPercentage ?? 0,
        };
      } else {
        updated[index] = emptyLine();
      }
      return updated;
    });
  };

  // ── Barcode scan ───────────────────────────────────────────────────────────
  const handleBarcodeScan = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();

    const sku = barcodeInput.trim();
    setBarcodeInput('');
    if (!sku) return;

    const item = salesItems.find(
      (s) => s.productSKU.toLowerCase() === sku.toLowerCase()
    );

    if (!item) {
      setBarcodeError(t('salesInvoice.barcodeNotFound', { sku }));
      setTimeout(() => setBarcodeError(null), 3000);
      barcodeRef.current?.focus();
      return;
    }

    setBarcodeError(null);

    setLines((prev) => {
      const updated = [...prev];
      const existingIdx = updated.findIndex((l) => l.productId === item.productId);
      if (existingIdx >= 0) {
        updated[existingIdx] = { ...updated[existingIdx], quantity: updated[existingIdx].quantity + 1 };
        return updated;
      }
      const emptyIdx = updated.findIndex((l) => l.productId === 0);
      const newLine: LineRow = {
        salesItemId:          item.salesItemId,
        productId:            item.productId,
        productName:          item.productName,
        productSKU:           item.productSKU,
        quantity:             1,
        unitPrice:            item.sellingPrice,
        minimumSellingPrice:  item.minimumSellingPrice ?? undefined,
        discountType:         'fixed',
        discountValue:        0,
        taxRate:              item.taxPercentage ?? 0,
      };
      if (emptyIdx >= 0) { updated[emptyIdx] = newLine; } else { updated.push(newLine); }
      return updated;
    });

    barcodeRef.current?.focus();
  };

  // ── Line field change ──────────────────────────────────────────────────────
  const handleLineChange = (
    index: number,
    field: 'quantity' | 'unitPrice' | 'discountValue' | 'taxRate',
    value: string
  ) => {
    setLines((prev) => {
      const updated = [...prev];
      let parsed = parseFloat(value) || 0;

      if (field === 'unitPrice') {
        const floor = updated[index].minimumSellingPrice;
        if (floor !== undefined && floor !== null && parsed < floor) {
          parsed = floor;
        }
      }

      updated[index] = { ...updated[index], [field]: parsed };
      return updated;
    });
  };

  const handleDiscountTypeToggle = (index: number) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        discountType:  updated[index].discountType === 'fixed' ? 'percent' : 'fixed',
        discountValue: 0,
      };
      return updated;
    });
  };

  const addLine        = () => setLines((prev) => [...prev, emptyLine()]);
  const addServiceLine = () => setLines((prev) => [...prev, emptyServiceLine()]);
  const removeLine     = (index: number) => {
    if (lines.length > 1) setLines((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Service item select ────────────────────────────────────────────────────
  const handleServiceItemSelect = (index: number, serviceItemIdStr: string) => {
    const item = serviceItems.find((s) => s.id === Number(serviceItemIdStr));
    setLines((prev) => {
      const updated = [...prev];
      if (item) {
        updated[index] = {
          ...updated[index],
          serviceItemId: item.id,
          productName:   item.name,
          productSKU:    item.code,
          description:   item.defaultDescription || '',
          unitPrice:     item.defaultPrice,
          taxRate:       item.taxRate ?? 0,
        };
      } else {
        updated[index] = emptyServiceLine();
      }
      return updated;
    });
  };

  // ── Discount helpers ───────────────────────────────────────────────────────
  const calcLineTotal = (line: LineRow): number => {
    const base      = line.quantity * line.unitPrice;
    const discAmt   = calcLineDiscountAmount(line);
    const afterDisc = base - discAmt;
    const taxAmt    = afterDisc * ((line.taxRate || 0) / 100);
    return afterDisc + taxAmt;
  };

  /** Product lines only — Service lines carry no sales item and so no floor. */
  const isLineBelowMinimum = (line: LineRow): boolean =>
    line.lineType === 'Product' && isBelowMinimumPrice(line, line.minimumSellingPrice);

  // ── Summary calculations ───────────────────────────────────────────────────
  const subtotal      = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);
  const totalDiscount = lines.reduce((sum, l) => sum + calcLineDiscountAmount(l), 0);
  const totalTax      = lines.reduce((sum, l) => {
    const afterDisc = l.quantity * l.unitPrice - calcLineDiscountAmount(l);
    return sum + afterDisc * ((l.taxRate || 0) / 100);
  }, 0);

  const preTotalForInvDisc = subtotal - totalDiscount + totalTax;
  const invoiceDiscountAmount = (() => {
    if (invoiceDiscount.value <= 0) return 0;
    if (invoiceDiscount.type === 'percent') {
      return preTotalForInvDisc * (Math.min(invoiceDiscount.value, 100) / 100);
    }
    return Math.min(invoiceDiscount.value, preTotalForInvDisc);
  })();

  const grandTotal = preTotalForInvDisc - invoiceDiscountAmount;

  // Keep amountTendered ≥ grandTotal by default
  useEffect(() => {
    setAmountTendered((prev) => (prev < grandTotal ? grandTotal : prev));
  }, [grandTotal]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    if (!formData.customerId) {
      setError(t('salesInvoice.formValidation.customerRequired'));
      return false;
    }
    if (!formData.invoiceDate) {
      setError(t('salesInvoice.formValidation.invoiceDateRequired'));
      return false;
    }
    if (!formData.dueDate) {
      setError(t('salesInvoice.formValidation.dueDateRequired'));
      return false;
    }
    const validLines = lines.filter((l) =>
      l.lineType === 'Service' ? l.serviceItemId != null : l.productId > 0
    );
    if (validLines.length === 0) {
      setError(t('salesInvoice.formValidation.itemsRequired'));
      return false;
    }
    for (const l of validLines) {
      if (l.quantity <= 0) {
        setError(t('salesInvoice.formValidation.quantityRequired'));
        return false;
      }
      if (l.unitPrice < 0) {
        setError(t('salesInvoice.formValidation.unitPriceInvalid'));
        return false;
      }
      if (
        l.lineType === 'Product' &&
        l.minimumSellingPrice !== undefined &&
        l.minimumSellingPrice !== null
      ) {
        if (l.unitPrice < l.minimumSellingPrice) {
          setError(
            t('salesInvoice.formValidation.unitPriceBelowMinimum', {
              product: l.productName,
              min: l.minimumSellingPrice,
            })
          );
          return false;
        }
        if (isLineBelowMinimum(l)) {
          setError(
            t('salesInvoice.formValidation.netPriceBelowMinimum', {
              product: l.productName,
              min: l.minimumSellingPrice,
              net: calcNetUnitPrice(l).toFixed(2),
            })
          );
          return false;
        }
      }
    }
    return true;
  };

  // ── Build lines payload ────────────────────────────────────────────────────
  const buildLinesPayload = (): CreateSalesInvoiceLineRequest[] => {
    const validLines = lines.filter((l) =>
      l.lineType === 'Service' ? l.serviceItemId != null : l.productId > 0
    );
    const baseForDistribution = validLines.reduce(
      (sum, l) => sum + (l.quantity * l.unitPrice - calcLineDiscountAmount(l)),
      0
    );
    return validLines.map((l) => {
      const lineDiscAmt  = calcLineDiscountAmount(l);
      const lineBase     = l.quantity * l.unitPrice - lineDiscAmt;
      const share        = baseForDistribution > 0 ? lineBase / baseForDistribution : 1 / validLines.length;
      const invDiscShare = invoiceDiscountAmount * share;
      if (l.lineType === 'Service') {
        return {
          lineType:       'Service' as const,
          serviceItemId:  l.serviceItemId,
          description:    l.description || l.productName,
          quantity:       l.quantity,
          unitPrice:      l.unitPrice,
          discountAmount: parseFloat((lineDiscAmt + invDiscShare).toFixed(4)),
          taxRate:        l.taxRate || 0,
        };
      }
      return {
        lineType:       'Product' as const,
        salesItemId:    l.salesItemId,
        productId:      l.productId,
        quantity:       l.quantity,
        unitPrice:      l.unitPrice,
        discountAmount: parseFloat((lineDiscAmt + invDiscShare).toFixed(4)),
        taxRate:        l.taxRate || 0,
      };
    });
  };

  // ── Cashier POS: clear the form for the next customer ───────────────────────
  const resetForNextSale = (message: string) => {
    setLines([emptyLine()]);
    setInvoiceDiscount({ type: 'fixed', value: 0 });
    setAmountTendered(0);
    setReferenceNumber('');
    setInvoiceRefNumber('');
    setBarcodeInput('');
    setBarcodeError(null);
    setError(null);
    const defaultCustomer = customers.find((c) => c.isDefault);
    setFormData((prev) => ({
      ...prev,
      customerId: defaultCustomer ? String(defaultCustomer.id) : '',
      notes: '',
    }));
    setPosMessage(message);
    setPrinting(false);
    barcodeRef.current?.focus();
  };

  // ── Print & Pay ────────────────────────────────────────────────────────────
  // Two paths based on payment category:
  //   • Credit  → create credit sale (IsCredit=true) + issue + print
  //               → invoice appears in Outstanding Invoices for later settlement
  //   • Others  → createAndPay (settle immediately) + print
  const handlePrintAndPay = async () => {
    setPosMessage(null);
    setError(null);
    if (!validateForm()) return;

    if (isCashPayment && amountTendered < grandTotal) {
      setError(t('salesInvoice.formValidation.amountTenderedRequired'));
      return;
    }

    // Open PDF tab SYNCHRONOUSLY — gesture context lost after first await
    const pdfTab = window.open('', '_blank');

    setPrinting(true);
    try {
      if (isCreditPayment) {
        // ── Credit sale: create → issue → print (no payment recorded) ────────
        const creditPayload: CreateSalesInvoiceRequest = {
          customerId:      Number(formData.customerId),
          invoiceDate:     formData.invoiceDate,
          dueDate:         formData.dueDate,
          notes:           formData.notes || undefined,
          referenceNumber: invoiceRefEnabled ? invoiceRefNumber || undefined : undefined,
          isCredit:        true,
          lines:           buildLinesPayload(),
        };

        const createRes = await salesInvoiceService.createSalesInvoice(creditPayload);
        const created   = createRes.data?.data;

        if (created) {
          await salesInvoiceService.issueSalesInvoice(created.id);
          await salesInvoiceService.viewInvoicePdf(created.id, created.invoiceNumber, pdfTab);
        }

        const creditMsg = t('salesInvoice.creditSaleCreated', { number: created?.invoiceNumber ?? '' });
        if (cashierMode) { resetForNextSale(creditMsg); return; }
        navigate('/sales/sales-invoices', { state: { message: creditMsg } });
      } else {
        // ── Cash / Card / Other: immediate POS settlement ─────────────────────
        const posPayload: CreateAndPaySalesInvoiceRequest = {
          customerId:             Number(formData.customerId),
          invoiceDate:            formData.invoiceDate,
          dueDate:                formData.dueDate,
          notes:                  formData.notes || undefined,
          referenceNumber:        invoiceRefEnabled ? invoiceRefNumber || undefined : undefined,
          isCredit:               false,
          lines:                  buildLinesPayload(),
          amountPaid:             isCashPayment ? amountTendered : grandTotal,
          tenderedAmount:         isCashPayment ? amountTendered : undefined,
          paymentMethod,
          paymentReferenceNumber: referenceNumber || undefined,
        };

        const res     = await salesInvoiceService.createAndPaySalesInvoice(posPayload);
        const created = res.data?.data;

        if (created) {
          await salesInvoiceService.viewInvoicePdf(created.id, created.invoiceNumber, pdfTab);
        }

        const paidMsg = t('salesInvoice.paidSuccess', { number: created?.invoiceNumber ?? '' });
        if (cashierMode) { resetForNextSale(paidMsg); return; }
        navigate('/sales/sales-invoices', { state: { message: paidMsg } });
      }
    } catch (err: unknown) {
      if (pdfTab && !pdfTab.closed) pdfTab.close();

      const parsed = parseApiError(err, t('salesInvoice.failedToPay'));
      const code   = (err as { response?: { data?: { error?: { code?: string } } } })
                       .response?.data?.error?.code;
      const isDayEndRequired = code === 'DAY_END_REQUIRED' || parsed.message.includes('DAY_END_REQUIRED');
      if (isDayEndRequired) setDayEndRequired(true);
      setError(
        isDayEndRequired
          ? t('salesInvoice.dayEndRequired', 'This outlet’s trading day is still open and past its close cutoff. Day-end must be run before any more sales.')
          : code === 'TILL_NOT_OPEN'
            ? t('salesInvoice.tillNotOpen')
            : parsed.message
      );
      setPrinting(false);
    }
  };

  const fmt            = useCurrencyFormat();
  const validItemCount = lines.filter((l) =>
    l.lineType === 'Service' ? l.serviceItemId != null : l.productId > 0
  ).length;

  return (
    <PageShell cashier={cashierMode}>
      <div className="si-create-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            {!cashierMode && (
              <button
                type="button"
                className="ims-back-btn"
                onClick={() => navigate('/sales/sales-invoices')}
              >
                <ArrowLeft size={14} strokeWidth={2} />
                {t('salesInvoice.backToList')}
              </button>
            )}
            <div>
              <h1 className="ims-page-title">{t('salesInvoice.createTitle')}</h1>
              <p className="ims-page-subtitle">{t('salesInvoice.createSubtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => (cashierMode
                ? resetForNextSale('')
                : navigate('/sales/sales-invoices'))}
              disabled={printing}
            >
              <Undo2 size={18} strokeWidth={1.75} />
              {cashierMode ? t('salesInvoice.newSale') : t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn-save"
              onClick={handlePrintAndPay}
              disabled={printing || dayEndRequired}
            >
              {printing ? (
                <>
                  <span className="ims-btn-spinner" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Printer size={18} strokeWidth={1.75} />
                  {t('salesInvoice.printAndPay')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Day-end-required block banner ── */}
        {dayEndRequired && (
          <div className="ims-alert ims-alert-error" role="alert">
            <strong>{t('salesInvoice.dayEndRequiredTitle', 'Day-End Required')}</strong>{' '}
            {t('salesInvoice.dayEndRequired', 'This outlet’s trading day is still open and past its close cutoff. Day-end must be run before any more sales.')}
          </div>
        )}

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >✕</button>
          </div>
        )}

        {/* ── POS success banner (cashier mode) ── */}
        {cashierMode && posMessage && (
          <div className="ims-alert ims-alert-success">
            {posMessage}
            <button
              type="button"
              onClick={() => setPosMessage(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >✕</button>
          </div>
        )}

        {/* ── 3-column body — disabled when day-end is required ── */}
        <div
          className="ims-create-body"
          style={{
            opacity: dayEndRequired ? 0.5 : 1,
            pointerEvents: dayEndRequired ? 'none' : 'auto',
            transition: 'opacity 0.2s ease',
          }}
        >

          {/* ══ LEFT — Invoice Info ══ */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('salesInvoice.invoiceInfo')}</h2>
              </div>
              <div className="ims-card-body">

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('salesInvoice.customer')} <span className="required">*</span>
                  </label>
                  <select
                    className="ims-form-control"
                    name="customerId"
                    value={formData.customerId}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">{t('salesInvoice.selectCustomer')}</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ims-date-row">
                  <div className="ims-form-group">
                    <label className="ims-form-label">
                      {t('salesInvoice.invoiceDate')} <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className="ims-form-control"
                      name="invoiceDate"
                      value={formData.invoiceDate}
                      readOnly
                      style={{ backgroundColor: 'var(--ims-input-disabled-bg, #f3f4f6)', cursor: 'default' }}
                    />
                  </div>
                  <div className="ims-form-group">
                    <label className="ims-form-label">
                      {t('salesInvoice.dueDate')} <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className="ims-form-control"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                {invoiceRefEnabled && (
                  <div className="ims-form-group">
                    <label className="ims-form-label">{invoiceRefLabel}</label>
                    <input
                      type="text"
                      className="ims-form-control"
                      value={invoiceRefNumber}
                      onChange={(e) => setInvoiceRefNumber(e.target.value)}
                      maxLength={100}
                    />
                  </div>
                )}

                <div className="ims-form-group">
                  <label className="ims-form-label">{t('salesInvoice.notes')}</label>
                  <textarea
                    className="ims-form-control ims-textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder={t('salesInvoice.notesPlaceholder')}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* ══ CENTER — Line Items ══ */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('salesInvoice.lineItems')}</h2>
                {loadingItems && (
                  <span style={{ fontSize: 12, color: 'var(--ims-text-muted)' }}>{t('common.loading')}</span>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="ims-btn ims-btn-secondary" onClick={addLine}>
                    <Plus size={13} strokeWidth={2} />
                    {t('salesInvoice.addProductLine')}
                  </button>
                  {serviceItems.length > 0 && (
                    <button type="button" className="ims-btn ims-btn-secondary" onClick={addServiceLine}>
                      <Wrench size={13} strokeWidth={2} />
                      {t('salesInvoice.addServiceLine')}
                    </button>
                  )}
                </div>
              </div>

              {/* ── Barcode scanner row ── */}
              <div className="si-barcode-row">
                <span className="si-barcode-icon" aria-hidden="true">
                  <Scan size={18} strokeWidth={1.8} />
                </span>
                <input
                  ref={barcodeRef}
                  type="text"
                  className={`si-barcode-input${barcodeError ? ' si-barcode-input--error' : ''}`}
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={handleBarcodeScan}
                  placeholder={t('salesInvoice.barcodePlaceholder')}
                  autoComplete="off"
                  spellCheck={false}
                />
                {barcodeError ? (
                  <span className="si-barcode-msg si-barcode-msg--error">{barcodeError}</span>
                ) : (
                  <span className="si-barcode-msg">{t('salesInvoice.barcodeHint')}</span>
                )}
              </div>

              <div className="ims-lines-body">
                <div className="ims-table-scroll">
                  <table className="ims-table">
                    <thead>
                      <tr>
                        <th className="ims-th">{t('salesInvoice.product')}</th>
                        <th className="ims-th ims-th--num ims-td--qty">{t('salesInvoice.quantity')}</th>
                        <th className="ims-th ims-th--num ims-td--unit-price">{t('salesInvoice.unitPrice')}</th>
                        <th className="ims-th ims-th--num ims-td--discount-val">{t('salesInvoice.discount')}</th>
                        <th className="ims-th ims-th--num ims-td--tax-rate">{t('salesInvoice.taxRatePct')}</th>
                        <th className="ims-th ims-th--num">{t('salesInvoice.lineTotal')}</th>
                        <th className="ims-th ims-th--del" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => (
                        <tr key={index} className="ims-tr">
                          <td className="ims-td">
                            {line.lineType === 'Service' ? (
                              <select
                                className="ims-cell-input"
                                value={line.serviceItemId ?? ''}
                                onChange={(e) => handleServiceItemSelect(index, e.target.value)}
                              >
                                <option value="">{t('salesInvoice.selectServiceItem')}</option>
                                {serviceItems.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name} ({s.code})
                                  </option>
                                ))}
                              </select>
                            ) : salesItems.length > 0 ? (
                              <select
                                className="ims-cell-input"
                                value={line.salesItemId ?? ''}
                                onChange={(e) => handleLineItemSelect(index, e.target.value)}
                              >
                                <option value="">{t('salesInvoice.selectProduct')}</option>
                                {salesItems.map((s) => (
                                  <option key={s.salesItemId} value={s.salesItemId}>
                                    {s.productName} ({s.productSKU})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className="ims-cell-input"
                                value={line.productName}
                                placeholder={t('salesInvoice.productNamePlaceholder')}
                                readOnly
                              />
                            )}
                          </td>

                          <td className="ims-td">
                            <input
                              ref={setCellRef(index, 0)}
                              type="number"
                              className="ims-cell-input ims-cell-input--num ims-td--qty"
                              value={line.quantity}
                              min={1}
                              onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                              onKeyDown={(e) => handleCellKeyDown(e, index, 0)}
                            />
                          </td>

                          <td className="ims-td">
                            <input
                              ref={setCellRef(index, 1)}
                              type="number"
                              className="ims-cell-input ims-cell-input--num ims-td--unit-price"
                              value={line.unitPrice}
                              min={line.minimumSellingPrice ?? 0}
                              step="0.01"
                              onChange={(e) => handleLineChange(index, 'unitPrice', e.target.value)}
                              onKeyDown={(e) => handleCellKeyDown(e, index, 1)}
                              title={
                                line.minimumSellingPrice != null
                                  ? t('salesInvoice.minPriceHint', { min: line.minimumSellingPrice })
                                  : undefined
                              }
                            />
                            {line.minimumSellingPrice != null && line.minimumSellingPrice > 0 && (
                              <small
                                style={{
                                  display: 'block',
                                  fontSize: '10px',
                                  color: isLineBelowMinimum(line)
                                    ? 'var(--ims-red-text)'
                                    : 'var(--ims-text-muted)',
                                  marginTop: '2px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t('salesInvoice.minPriceHint', { min: fmt(line.minimumSellingPrice) })}
                              </small>
                            )}
                          </td>

                          <td className="ims-td">
                            <div className="si-discount-cell">
                              <input
                                ref={setCellRef(index, 2)}
                                type="number"
                                className="ims-cell-input ims-cell-input--num ims-td--discount-val"
                                value={line.discountValue || ''}
                                min={0}
                                max={line.discountType === 'percent' ? 100 : undefined}
                                step="0.01"
                                placeholder="0"
                                onChange={(e) => handleLineChange(index, 'discountValue', e.target.value)}
                                onKeyDown={(e) => handleCellKeyDown(e, index, 2)}
                              />
                              <button
                                type="button"
                                className={`si-disc-type-btn${line.discountType === 'percent' ? ' active' : ''}`}
                                onClick={() => handleDiscountTypeToggle(index)}
                                title={
                                  line.discountType === 'fixed'
                                    ? t('salesInvoice.switchToPercent')
                                    : t('salesInvoice.switchToFixed')
                                }
                              >
                                {line.discountType === 'percent' ? '%' : 'V'}
                              </button>
                            </div>
                          </td>

                          <td className="ims-td">
                            <input
                              type="number"
                              className="ims-cell-input ims-cell-input--num ims-td--tax-rate"
                              value={line.taxRate || 0}
                              readOnly
                              tabIndex={-1}
                              style={{ background: 'var(--ims-bg-subtle, #f9fafb)', color: 'var(--ims-text-muted)', cursor: 'not-allowed' }}
                            />
                          </td>

                          <td className="ims-td ims-td--line-total">
                            {fmt(calcLineTotal(line))}
                          </td>

                          <td className="ims-td">
                            <button
                              type="button"
                              className="ims-remove-btn"
                              onClick={() => removeLine(index)}
                              disabled={lines.length === 1}
                              title={t('common.remove')}
                            >
                              <CircleX size={18} color="#991b1b" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* ══ RIGHT — Summary + Payment ══ */}
          <div className="ims-panel-right">
            {/* Invoice summary */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('salesInvoice.summary')}</h2>
              </div>
              <div className="ims-card-body">
                <div className="si-payment-summary">

                  <div className="si-payment-row">
                    <span>{t('salesInvoice.lineItemCount')}</span>
                    <strong>{validItemCount}</strong>
                  </div>

                  <div className="si-payment-row">
                    <span>{t('salesInvoice.subtotal')}</span>
                    <strong>{fmt(subtotal)}</strong>
                  </div>

                  {totalDiscount > 0 && (
                    <div className="si-payment-row" style={{ color: 'var(--ims-amber-text)' }}>
                      <span>{t('salesInvoice.lineDiscount')}</span>
                      <strong>-{fmt(totalDiscount)}</strong>
                    </div>
                  )}

                  {totalTax > 0 && (
                    <div className="si-payment-row">
                      <span>{t('salesInvoice.tax')}</span>
                      <strong>{fmt(totalTax)}</strong>
                    </div>
                  )}

                  {/* Invoice-level discount */}
                  <div className="si-payment-row si-inv-disc-row">
                    <span>{t('salesInvoice.invoiceDiscount')}</span>
                    <div className="si-inv-disc-ctrl">
                      <button
                        type="button"
                        className={`si-disc-type-btn${invoiceDiscount.type === 'percent' ? ' active' : ''}`}
                        onClick={() =>
                          setInvoiceDiscount((prev) => ({
                            type:  prev.type === 'fixed' ? 'percent' : 'fixed',
                            value: 0,
                          }))
                        }
                        title={
                          invoiceDiscount.type === 'fixed'
                            ? t('salesInvoice.switchToPercent')
                            : t('salesInvoice.switchToFixed')
                        }
                      >
                        {invoiceDiscount.type === 'percent' ? '%' : 'V'}
                      </button>
                      <input
                        type="number"
                        className="si-inv-disc-num"
                        value={invoiceDiscount.value || ''}
                        min={0}
                        max={invoiceDiscount.type === 'percent' ? 100 : undefined}
                        step="0.01"
                        placeholder="0"
                        onChange={(e) =>
                          setInvoiceDiscount((prev) => ({
                            ...prev,
                            value: parseFloat(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>
                  </div>

                  {invoiceDiscountAmount > 0 && (
                    <div className="si-payment-row" style={{ color: 'var(--ims-amber-text)', fontSize: 12 }}>
                      <span>{t('salesInvoice.invoiceDiscountAmt')}</span>
                      <strong>-{fmt(invoiceDiscountAmount)}</strong>
                    </div>
                  )}

                  <div className="si-payment-row si-payment-row--total">
                    <span>{t('salesInvoice.totalAmount')}</span>
                    <strong>{fmt(grandTotal)}</strong>
                  </div>

                </div>
              </div>
            </div>

            {/* Payment method */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('salesInvoice.posPaymentTitle')}</h2>
              </div>
              <div className="ims-card-body">
                <div className="si-payment-summary">

                  <div className="ims-form-group" style={{ marginBottom: 12 }}>
                    <label className="ims-form-label">{t('salesInvoice.paymentMethod')}</label>
                    <select
                      className="ims-form-control"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      {paymentTypes.length === 0 ? (
                        <option value={paymentMethod}>{paymentMethod || t('common.loading')}</option>
                      ) : (
                        paymentTypes.map((pt) => (
                          <option key={pt.id} value={pt.code}>
                            {pt.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Cash: tendered + change */}
                  {isCashPayment && (
                    <>
                      <div className="ims-form-group" style={{ marginBottom: 8 }}>
                        <label className="ims-form-label">{t('salesInvoice.amountTendered')}</label>
                        <input
                          type="number"
                          className="ims-form-control"
                          value={amountTendered || ''}
                          min={grandTotal}
                          step="0.01"
                          onChange={(e) => setAmountTendered(parseFloat(e.target.value) || 0)}
                        />
                        <div className="si-quick-amounts">
                          {[100, 500, 1000, 5000].map((amt) => (
                            <button
                              key={amt}
                              type="button"
                              className="si-quick-amt-btn"
                              onClick={() => setAmountTendered(amt)}
                            >
                              {amt.toLocaleString()}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="si-payment-row" style={{
                        background: 'var(--ims-green-50, #f0fdf4)',
                        borderRadius: 6,
                        padding: '8px 10px',
                        marginTop: 4,
                      }}>
                        <span style={{ fontWeight: 600 }}>{t('salesInvoice.changeAmount')}</span>
                        <strong style={{ color: 'var(--ims-green-700, #15803d)', fontSize: 16 }}>
                          {fmt(Math.max(0, amountTendered - grandTotal))}
                        </strong>
                      </div>
                    </>
                  )}

                  {/* Credit: info box — customer pays later */}
                  {isCreditPayment && (
                    <div style={{
                      background: '#ecf9fc',
                      border: '1px solid #06b6d4',
                      borderRadius: 8,
                      padding: '12px 14px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <Info size={16} strokeWidth={2} color="#0e7490" />
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#0e7490' }}>
                          {t('salesInvoice.creditSaleMode') || 'Credit Sale'}
                        </span>
                      </div>
                      <p style={{ fontSize: 12, color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                        {t('salesInvoice.creditSaleModeHelper') ||
                          'Customer takes goods now and pays later. This invoice will be issued and appear in Outstanding Invoices for settlement.'}
                      </p>
                    </div>
                  )}

                  {/* Non-cash, non-credit: reference number */}
                  {!isCashPayment && !isCreditPayment && (
                    <div className="ims-form-group" style={{ marginBottom: 8 }}>
                      <label className="ims-form-label">{t('salesInvoice.referenceNumber')}</label>
                      <input
                        type="text"
                        className="ims-form-control"
                        value={referenceNumber}
                        placeholder={t('salesInvoice.referencePlaceholder')}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                      />
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </PageShell>
  );
};

export default SalesInvoiceCreatePage;
